import Card from "@mui/material/Card";
import CardMedia from "@mui/material/CardMedia";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import LoadingButton from "@mui/lab/LoadingButton";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import { makeStyles } from "@mui/styles";
import { Grid, Alert } from "@mui/material";
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../services/api";
import logo from "../../assets/img/pibs_logo_2.png";

const useStyles = makeStyles({
  root: {
    borderRadius: 16,
    boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.5)",
  },
});

export default function VerifyAccountCreationOtp() {
  const classes = useStyles();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [admissionNumber, setAdmissionNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [loadData, setLoadData] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [otpError, setOtpError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Extract admission number from URL parameters on component mount
  useEffect(() => {
    const admissionNoFromUrl = searchParams.get("admissionNo");
    if (admissionNoFromUrl) {
      setAdmissionNumber(admissionNoFromUrl);
    }
  }, [searchParams]);

  const sendOtp = async () => {
    setSendingOtp(true);
    setErrorMessage("");
    setSuccessMessage("");

    if (admissionNumber.length < 4) {
      setErrorMessage("Admission number must be at least 4 characters long.");
      setSendingOtp(false);
      return;
    }

    try {
      const response = await api({
        url: `/auth/student/account-creation-otp/${admissionNumber}`,
        method: "GET",
      });

      if (response.status === 200) {
        setSendingOtp(false);
        setSuccessMessage(
          response.data.data.message ||
            "OTP sent successfully to your registered device."
        );
        console.log("OTP Response:", response.data);
      }
    } catch (error) {
      setSendingOtp(false);
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        setErrorMessage(error.response.data.message);
      } else {
        setErrorMessage("Failed to send OTP. Please try again.");
      }
      console.log("Send OTP Error:", error);
    }
  };

  const validateOtp = () => {
    setLoadData(true);
    setErrorMessage("");
    setSuccessMessage("");

    if (otp.length < 4) {
      setOtpError(true);
      setLoadData(false);
      setErrorMessage("OTP must be at least 4 characters long.");
    } else {
      setOtpError(false);
      setLoadData(true);
      try {
        api({
          url: `/auth/student/validate-otp`,
          method: "POST",
          data: { admission_number: admissionNumber, otp: otp },
        })
          .then((res) => {
            if (res.status === 200 && res.data.success) {
              setLoadData(false);
              console.log(res.data.message);
              sessionStorage.setItem(
                "validationToken",
                res.data.data.validation_token
              );
              sessionStorage.setItem(
                "admissionNumber",
                res.data.data.admission_number
              );
              navigate("/auth/set-password");
            } else {
              setLoadData(false);
              setErrorMessage(res.data.message || "OTP validation failed.");
            }
          })
          .catch((err) => {
            setLoadData(false);
            if (
              err.response &&
              err.response.data &&
              err.response.data.message
            ) {
              setErrorMessage(err.response.data.message);
            } else {
              setErrorMessage("OTP validation failed. Please try again.");
            }
            console.log(err);
          });
      } catch (error) {
        setLoadData(false);
        setErrorMessage("An unexpected error occurred. Please try again.");
        console.log(error);
      }
    }
  };

  return (
    <div className="h-screen flex items-center justify-center px-2 bg-gradient-to-b from-gray-100 to-gray-300">
      <Card
        className={classes.root}
        sx={{ minWidth: 300, maxWidth: 400 }}
        variant="outlined"
      >
        <CardMedia
          sx={{ height: 72, width: 72, margin: "auto", marginTop: "10px" }}
          component="img"
          image={logo}
        />
        <CardContent>
          <Typography gutterBottom variant="h5" component="div" align="center">
            PIBS Student Portal Login
          </Typography>
          <Grid container rowGap={3} marginTop={3}>
            {errorMessage && (
              <Alert severity="error" sx={{ width: "100%" }}>
                {errorMessage}
              </Alert>
            )}
            {successMessage && (
              <Alert severity="success" sx={{ width: "100%" }}>
                {successMessage}
              </Alert>
            )}
            <div style={{ width: "100%", display: "flex", gap: "8px" }}>
              <TextField
                required
                fullWidth
                label="Admission Number"
                value={admissionNumber}
                onChange={(e) => {
                  setAdmissionNumber(e.target.value);
                  e.preventDefault();
                }}
                size="small"
                disabled
              />
              <Button
                variant="outlined"
                size="small"
                onClick={sendOtp}
                disabled={sendingOtp || admissionNumber.length < 4}
                sx={{ minWidth: "80px", whiteSpace: "nowrap" }}
              >
                {sendingOtp ? "Sending..." : "Send OTP"}
              </Button>
            </div>
            <TextField
              required
              fullWidth
              label="OTP Verification Code"
              value={otp}
              onChange={(e) => {
                setOtp(e.target.value);
                e.preventDefault();
              }}
              size="small"
              error={otpError}
              helperText="Please fill in the OTP (One Time Password) sent to your device."
            />
          </Grid>
        </CardContent>
        <CardActions>
          <Grid
            container
            direction="column"
            justifyContent="center"
            alignItems="center"
            rowGap={1}
          >
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <LoadingButton
                loading={loadData}
                size="medium"
                variant="contained"
                onClick={validateOtp}
                loadingIndicator="..."
              >
                <span>Verify</span>
              </LoadingButton>
            </div>
          </Grid>
        </CardActions>
      </Card>
    </div>
  );
}
